import { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  setElevatedAuth,
  selectIsTotpElevated,
  selectHasPendingVerifications,
  setHasPendingVerifications,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import VerifyCodeModal from 'src/components/NewRenterInterface/AccountHub/VerifyCodeModal';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';
import type { CertMember } from 'src/types/Certification';

interface MemberContactEditorProps {
  /** The household member whose contact information is being edited. */
  member: CertMember;
  /** True when the acting user is editing their own contacts. */
  isSelf?: boolean;
  /**
   * Whether the parent has resolved the authenticator status. When false
   * (still loading, or the status request failed) we can't tell whether the
   * user has an authenticator, so we open the verify prompt rather than
   * rendering nothing. The backend remains the authoritative gate.
   */
  statusKnown?: boolean;
  /** Called when the user dismisses the authentication prompt. */
  onDismiss?: () => void;
}

/**
 * Authenticator step-up gate for editing another household member's contacts.
 *
 * Renders the "Verify Your Identity" modal when the renter has an authenticator
 * configured but the current session isn't TOTP-elevated. The parent owns the
 * actual contact editor (rendered when elevated) and the 2FA setup blocker, so
 * this component renders nothing in those states.
 */
const MemberContactEditor: FC<MemberContactEditorProps> = ({
  member,
  isSelf = false,
  statusKnown = true,
  onDismiss,
}) => {
  const dispatch = useDispatch();
  const [showPendingVerifications, setShowPendingVerifications] =
    useState(false);

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const elevatedAuth = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.elevatedAuth
  );
  const isTotpElevated = useSelector(selectIsTotpElevated);
  const hasPendingVerifications = useSelector(selectHasPendingVerifications);

  const hasRealEmail =
    !!user?.email && !user.email.endsWith('@prontohousing.io');
  const hasUsableContact =
    (hasRealEmail && user?.email_verified === true) ||
    user?.phone_verified === true;
  const blockOnPending = hasPendingVerifications && !hasUsableContact;
  const otpFirstChannel: 'email' | 'sms' = hasRealEmail ? 'email' : 'sms';

  const needsTotpSetup = !elevatedAuth.totp_enabled;

  const memberFirstName = member.first_name?.trim();
  const verifyDescription = isSelf
    ? 'To change your contact information, verify your identity with your authenticator app.'
    : `To change ${
        memberFirstName ? `${memberFirstName}'s` : "this member's"
      } contact information, verify your identity with your authenticator app.`;

  const handleVerifyDismiss = useCallback((): void => {
    setShowPendingVerifications(false);
    onDismiss?.();
  }, [onDismiss]);

  // The parent owns the editor (elevated) and the 2FA setup blocker (no
  // authenticator), and there is nothing to edit without a user account.
  if (member.user_id == null || isTotpElevated) {
    return null;
  }
  // Only suppress the prompt when we *know* the user has no authenticator (the
  // parent shows the setup blocker in that case). If the status is unresolved,
  // still show the prompt so clicking Edit is never a no-op.
  if (statusKnown && needsTotpSetup) {
    return null;
  }

  // Authenticator configured but the session isn't TOTP-elevated — go straight
  // to the Verify Your Identity modal (no interstitial card).
  return (
    <>
      {!showPendingVerifications && !blockOnPending && (
        <VerifyCodeModal
          purpose="elevated_auth"
          totpEnabled={true}
          preferredChannel={otpFirstChannel}
          hideFallback={true}
          description={verifyDescription}
          onSuccess={(expiresAt, method) => {
            dispatch(
              setElevatedAuth({
                verified: true,
                expires_at: expiresAt,
                verified_at: new Date().toISOString(),
                method,
              })
            );
          }}
          onCancel={handleVerifyDismiss}
          onUnverifiedContact={() => setShowPendingVerifications(true)}
          dismissable
        />
      )}

      {(showPendingVerifications || blockOnPending) && (
        <PendingVerificationsModal
          onAllVerified={() => {
            dispatch(setHasPendingVerifications(false));
            setShowPendingVerifications(false);
          }}
          onDismiss={handleVerifyDismiss}
        />
      )}
    </>
  );
};

export default MemberContactEditor;
